"use client";

import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

import CaseManager from "../../../styles/case-manager-modules/caseManagerDashboard.module.css";
import styles from "../../../styles/patient-page-modules/PatientDetail.module.css";
import PatientCompletion from "../../../components/case-manager/PatientCompletion";
import StatusCard from "../../../components/case-manager/StatusCard";
import { Clock, ArrowDown, AlertCircle } from "lucide-react";
import Heading from "../../../components/shared-modules/Heading";
import OutOfRange from "./review-escalation/OutOfRange";
import OverveiwReports from "./OverveiwReports";
import PriorityTableSection from "../../../components/case-manager/PriorityTable";
import Spinner from "../../../components/spinner";
import toast from "react-hot-toast";
import { BASE_URL } from "../../../utils/constants";
import useRoles from "../../../hooks/useRoles";
import { handleApiError } from "../../../utils/handleApiError";
import { getUserIdentity } from "../../../utils/authorization";
const CaseManagerDashboard = () => {
  const { roles, position } = useRoles();
  const [activeTab, setActiveTab] = useState("tasks");
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outOfRangeCount, setOutOfRangeCount] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const [userName, setUserName] = useState({ first_name: "", last_name: "" });
  const [loginTime, setLoginTime] = useState("");
  const totalPatients = patients.length;

  const outOfRangePercent =
    totalPatients > 0
      ? ((outOfRangeCount / totalPatients) * 100).toFixed(1)
      : 0;

  const completionPercent =
    totalPatients > 0
      ? (((totalPatients - outOfRangeCount) / totalPatients) * 100).toFixed(1)
      : 0;

  useEffect(() => {
    sessionStorage.setItem(
      "completion_percentage",
      completionPercent
    );
    sessionStorage.setItem(
      "out_of_range_percentage",
      outOfRangePercent.toString()
    );
  }, [completionPercent, outOfRangePercent]);


  const tableSectionRef = useRef(null);

  const isAccountAdmin = getUserIdentity()?.isAdmin === true || position === "account_admin";
  const isMedicalAssistant = getUserIdentity()?.isMedicalAssistant === true || position === "medical_assistant";
  const hasStaffPageTasksAccess = getUserIdentity()?.hasstaffPageTasksAccess === true;
  const hasStaffPageReportsAccess = getUserIdentity()?.hasstaffPageReportsAccess === true;
  const hasStaffPageEscalationLogAccess = getUserIdentity()?.hasstaffPageEscalationLogAccess === true;


  const tabs = [
    { id: "tasks", label: "Patients Overview" },
    {
      id: "reports",
      label: "Staff Audit Report",
    },
    { id: "escalation_log", label: "Review & Escalation Log" },
  ];

  const visibleTabs = tabs.filter((tab) => {
    // if (isMedicalAssistant) {
    //   return tab.id === "tasks";
    // }

    // if (isAccountAdmin) {
    //   return tab.id !== "reports";
    // }

    if (tab.id === "tasks" && !hasStaffPageTasksAccess) return false;
    if (tab.id === "reports" && !hasStaffPageReportsAccess) return false;
    if (tab.id === "escalation_log" && !hasStaffPageEscalationLogAccess) return false;

    return true;
  });

  const getToken = () => localStorage.getItem("authToken");
  const getTenant = () => sessionStorage.getItem("acc_id");

  const fetchPatients = async () => {
    const token = getToken();
    const tenant = getTenant();

    const url = `${BASE_URL}/patients/${tenant}/patients/?type=all`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        const patientData = await fetchPatients();
        const allPatients = patientData?.patients || [];

        setPatients(allPatients);
        setFilteredPatients(allPatients);

        const savedFilterState = sessionStorage.getItem("caseManagerFilterState");
        if (savedFilterState === "outOfRange") {
          const filtered = allPatients.filter(
            (patient) => patient.out_of_range
          );
          setFilteredPatients(filtered);
          setIsFiltered(true);

          const outOfRangePatientIds = filtered.map((patient) => patient.id);
          sessionStorage.setItem("_ap", JSON.stringify(outOfRangePatientIds));

        } else {
          const allPatientIds = allPatients.map((patient) => patient.id);
          sessionStorage.setItem("_ap", JSON.stringify(allPatientIds));
          setIsFiltered(false);
        }

        if (allPatients.length > 0) {
          const allPatientIds = allPatients.map((patient) => patient.id);
          sessionStorage.setItem("_ap", JSON.stringify(allPatientIds));
        } else {
          sessionStorage.setItem("_ap", JSON.stringify([]));
        }

        const outOfRangeCount = allPatients.filter(
          (p) => p.out_of_range
        ).length;
        setOutOfRangeCount(outOfRangeCount);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        toast.error("Failed to load dashboard data");
        sessionStorage.setItem("_ap", JSON.stringify([]));
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();

    const savedTab = sessionStorage.getItem("caseManagerActiveTab");
    if (savedTab && visibleTabs.some(tab => tab.id === savedTab)) {
      setActiveTab(savedTab);
    } else if (visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].id);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem("caseManagerActiveTab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (patients.length > 0) {
      const allPatientIds = patients.map((patient) => patient.id);
      sessionStorage.setItem("_ap", JSON.stringify(allPatientIds));
    }
  }, [patients]);

  useEffect(() => {
    const userInfo = localStorage.getItem("user");
    if (userInfo) {
      try {
        const parsedUser = JSON.parse(userInfo);
        setUserName({
          first_name: parsedUser.first_name || "",
          last_name: parsedUser.last_name || "",
        });
      } catch (error) {
        console.error("Error parsing user info:", error);
      }
    }

    const now = new Date();
    const formattedDate = now.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const formattedTime = now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    setLoginTime(`${formattedDate} - ${formattedTime}`);
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const toggleOutOfRangeFilter = () => {
    if (isFiltered) {
      // Show all patients
      setFilteredPatients(patients);
      setIsFiltered(false);

      // Save filter state
      sessionStorage.setItem("caseManagerFilterState", "allPatients");

      // Update _ap with all patient IDs
      const allPatientIds = patients.map((patient) => patient.id);
      sessionStorage.setItem("_ap", JSON.stringify(allPatientIds));
    } else {
      // Show only out of range patients
      const filtered = patients.filter((patient) => patient.out_of_range);
      setFilteredPatients(filtered);
      setIsFiltered(true);

      // Save filter state
      sessionStorage.setItem("caseManagerFilterState", "outOfRange");

      // Update _ap with only out of range patient IDs
      const outOfRangePatientIds = filtered.map((patient) => patient.id);
      sessionStorage.setItem("_ap", JSON.stringify(outOfRangePatientIds));

      if (tableSectionRef.current) {
        setTimeout(() => {
          tableSectionRef.current.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  };

  const tabContent = {
    tasks: (
      <>
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Spinner />
          </div>
        ) : (
          <>
            <div className={CaseManager.section_2}>
              <div className={CaseManager.section_2_left}>
                <Heading
                  title="Patient Completion"
                  bodyHeight="410px"
                  content={<PatientCompletion patients={patients} />}
                />
              </div>
              <div className={CaseManager.section_2_right}>
                <Heading
                  title="Reading Review"
                  bodyHeight="167px"
                  content={
                    <StatusCard
                      percentage={totalPatients}
                      time="Total Patients"
                      // description="Less than 20 mins to complete"
                      buttonText="View All"
                      buttonLink="/admin-case-manager/reading-reviews"
                      title="Task Status"
                    // Icon={Clock}
                    // ArrowIcon={ArrowDown}
                    />
                  }
                />
                <Heading
                  title="Out of Range"
                  bodyHeight="167px"
                  content={
                    <StatusCard
                      percentage={`${outOfRangeCount}`}
                      time="Critical Readings"
                      description={`${outOfRangeCount} Critical Readings that need attention`}
                      buttonText={isFiltered ? "All Patients" : "Out of Range"}
                      onClick={toggleOutOfRangeFilter}
                      title="Out of Range"
                      Icon={AlertCircle}
                    />
                  }
                />
              </div>
            </div>
            <div className={CaseManager.table_section} ref={tableSectionRef}>
              <Heading
                title={
                  isFiltered ? "Out of Range Patients" : "Priority Patients"
                }
                actions={{}}
                // actions={[
                //   <button key="csv" className={CaseManager.csv_button}>
                //     <Link
                //       to="/admin-case-manager/create-patient"
                //       className={CaseManager.top_btn}
                //     >
                //       + Create Patient
                //     </Link>
                //   </button>,
                // ]}
                bodyHeight="auto"
                content={<PriorityTableSection patients={filteredPatients} isOutOfRangePatient={true} />}
              />
            </div>
          </>
        )}
      </>
    ),

    reports: <OverveiwReports />,
    escalation_log: (
      <div id="out-of-range-section">
        <Heading title="Readings" content={<OutOfRange />} />
      </div>
    ),
  };

  return (
    <div className={styles.patient_page}>
      <div className={styles.name_container_spacer}></div>
      <div className={styles.name_container}>
        <div className={styles.name_wrapper}>
          <Link
            to="/dashboard"
            className={styles.back_btn}
            onClick={() => {
              sessionStorage.setItem("caseManagerActiveTab", "tasks");
            }}
            aria-label="Back to dashboard"
            title="Back to dashboard"
          >
            <FontAwesomeIcon icon={faArrowLeftLong} />
          </Link>
          <div className={styles.name_and_status}>
            <p>Welcome Back</p>
            <h4>
              <strong>
                {userName.first_name} {userName.last_name}
              </strong>
              &nbsp;&nbsp;&nbsp; ___ &nbsp;&nbsp;&nbsp;{loginTime}
            </h4>
          </div>
        </div>
      </div>

      <div className={styles.patient_page_container}>
        <div className={styles.tabs_container}>
          <div className={styles.tabs_toggler}>
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.active : ""
                  }`}
                onClick={() => handleTabChange(tab.id)}
                aria-selected={activeTab === tab.id}
                tabIndex={activeTab === tab.id ? 0 : -1}
              >
                {tab.label}
              </button>
            ))}

          </div>
        </div>
        <div className={styles.tab_content}>{tabContent[activeTab]}</div>
      </div>
    </div>
  );
};

export default CaseManagerDashboard;
